/**
 * Homepage motion.
 * Loaded after rhm-gsap.ts, which registers the plugins and assigns
 * window.gsap, window.ScrollTrigger, and window.SplitText.
 */

interface TimelineLike {
  to: (targets: object, vars: object, position?: number | string) => TimelineLike;
  duration: () => number;
  kill: () => void;
}

interface GsapLike {
  utils: {
    toArray: <T extends Element = HTMLElement>(targets: string | Element[] | NodeList, scope?: Element) => T[];
  };
  context: (fn: () => void, scope?: Element) => { revert: () => void };
  matchMedia: () => { add: (query: string, fn: () => void | (() => void)) => void };
  set: (targets: object, vars: object) => void;
  to: (targets: object, vars: object) => void;
  fromTo: (targets: object, from: object, to: object) => void;
  timeline: (vars?: object) => TimelineLike;
  delayedCall: (delay: number, callback: () => void) => { kill: () => void };
}

interface ScrollTriggerLike {
  config: (vars: object) => void;
  refresh: () => void;
}

interface SplitResult {
  lines: HTMLElement[];
  masks: HTMLElement[];
}

interface SplitTextLike {
  create: (targets: Element[], vars: object) => SplitResult;
}

declare global {
  interface Window {
    gsap?: GsapLike;
    ScrollTrigger?: ScrollTriggerLike;
    SplitText?: SplitTextLike;
  }
}

const SQUARE = '▪';
const LINE_FADE = 0.15;
const SCRAMBLE_DUR = 0.4;
const TOTAL_DUR = 1.6;
const COVER_DUR = 0.8;
const WORD_DUR = 2;
const WORD_STAGGER = 0.24;
const LINE_DUR = 1.25;
const LINE_STAGGER = 0.085;
const MARK_DUR = 0.8;
const TURN = 0.4;
const STEP_DUR = 0.3;
const HOLD = 1.2;
const EASE = 'power2.in';
const OPACITY_NEAR = 0.3;
const NEAR_STEPS = 1;
const OPACITY_FLOOR = 0.05;
const OPACITY_REACH = 6;
const VISIBLE_BELOW = 8;
function fontsReady() {
  if (!document.fonts?.ready) return Promise.resolve();
  return Promise.race([
    document.fonts.ready,
    new Promise<void>((resolve) => setTimeout(resolve, 400)),
  ]);
}

function once(el: HTMLElement | null): el is HTMLElement {
  if (!el || el.dataset.rhmMotion === 'on') return false;
  el.dataset.rhmMotion = 'on';
  return true;
}

function scrambleFrame(el: HTMLElement, text: string, progress: number) {
  if (progress >= 1) {
    el.textContent = text;
    return;
  }
  const locked = Math.floor(progress * text.length);
  let html = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === ' ' || i < locked) html += ch;
    else html += `<span class="rhm-hero__sq">${SQUARE}</span>`;
  }
  el.innerHTML = html;
}

function reliefItemOpacity(distance: number) {
  if (distance > VISIBLE_BELOW) return 0;
  const d = Math.abs(distance);
  if (d <= NEAR_STEPS) return 1 + (OPACITY_NEAR - 1) * (d / NEAR_STEPS);
  const t = Math.min((d - NEAR_STEPS) / (OPACITY_REACH - NEAR_STEPS), 1);
  return OPACITY_NEAR + (OPACITY_FLOOR - OPACITY_NEAR) * t;
}

function initHero(gsap: GsapLike, root: HTMLElement) {
  if (!once(root)) return;
  const lines = gsap.utils.toArray<HTMLElement>('[data-hero-scramble]', root);
  const shadow = root.querySelector<HTMLElement>('.rhm-hero__shadow');
  const brand = root.querySelector<HTMLElement>('.rhm-hero__brand');
  const originals = lines.map((line) => {
    const original = line.dataset.heroText ?? line.textContent ?? '';
    line.dataset.heroText = original;
    return original;
  });

  gsap.context(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: reduce)', () => {
      lines.forEach((line, i) => {
        line.textContent = originals[i] ?? '';
      });
      gsap.set(lines, { autoAlpha: 0 });
      if (shadow) gsap.set(shadow, { '--rhm-hero-cover': 1 });
      if (brand) gsap.set(brand, { opacity: 1 });
    });
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      lines.forEach((line) => {
        line.textContent = '';
      });
      gsap.set(lines, { autoAlpha: 0 });
      if (brand) gsap.set(brand, { opacity: 0 });

      const tl = gsap.timeline();
      const revealWindow = TOTAL_DUR * 0.8;
      lines.forEach((line, i) => {
        const original = originals[i] ?? '';
        const timePoint = (i / lines.length) * revealWindow;
        const proxy = { t: 0 };
        tl.to(line, { autoAlpha: 1, duration: LINE_FADE, ease: 'none' }, timePoint);
        tl.to(
          proxy,
          {
            t: 1,
            duration: SCRAMBLE_DUR,
            ease: 'none',
            onStart: () => scrambleFrame(line, original, 0),
            onUpdate: () => scrambleFrame(line, original, proxy.t),
            onComplete: () => scrambleFrame(line, original, 1),
          },
          timePoint + 0.05,
        );
      });
      const coverStart = tl.duration();
      if (shadow) {
        gsap.set(shadow, { '--rhm-hero-cover': 0 });
        tl.to(shadow, { '--rhm-hero-cover': 1, duration: COVER_DUR, ease: 'power2.out' }, coverStart);
      }
      tl.to(lines, { autoAlpha: 0, duration: COVER_DUR, ease: 'power2.out' }, coverStart);
      if (brand) {
        tl.to(
          brand,
          {
            opacity: 1,
            duration: COVER_DUR,
            ease: 'power2.out',
            onStart: () => {
              root.dataset.heroCoverStarted = 'true';
              root.dispatchEvent(new CustomEvent('rhm-hero-cover-start', { detail: { duration: COVER_DUR } }));
            },
          },
          coverStart,
        );
      }
    });
  }, root);
}

function initWordmark(gsap: GsapLike, flying: HTMLElement) {
  if (!once(flying)) return;
  const heroSlot = document.querySelector<HTMLElement>('[data-hero-brand-slot]');
  const sizer = flying.parentElement?.querySelector<HTMLElement>('.rhm-header__wordmark-text');
  if (!heroSlot || !sizer || !flying.parentElement) return;
  const parent = flying.parentElement;

  const getHeroSlotCentre = () => {
    const r = heroSlot.getBoundingClientRect();
    return { cx: r.left + r.width / 2, cy: r.top + window.scrollY + r.height / 2 };
  };
  const getFlyingNaturalCentre = () => {
    const pr = parent.getBoundingClientRect();
    return { cx: pr.left + pr.width / 2, cy: pr.top + pr.height / 2 };
  };
  const getEndScale = () =>
    parseFloat(getComputedStyle(sizer).fontSize) / parseFloat(getComputedStyle(flying).fontSize);

  gsap.context(() => {
    const mm = gsap.matchMedia();
    mm.add('(min-width: 760px)', () => {
      heroSlot.setAttribute('aria-hidden', 'true');
      heroSlot.style.visibility = 'hidden';
      gsap.set(flying, {
        xPercent: -50,
        yPercent: -50,
        x: 0,
        y: 0,
        scale: getEndScale(),
        letterSpacing: '0.06em',
        opacity: 1,
      });
      return () => {
        heroSlot.removeAttribute('aria-hidden');
        heroSlot.style.visibility = '';
        gsap.set(flying, { clearProps: 'all' });
      };
    });
    mm.add('(min-width: 760px) and (prefers-reduced-motion: no-preference)', () => {
      const hero = document.getElementById('hero');
      gsap.set(flying, { opacity: 0 });
      const reveal = (event?: Event) => {
        const detail = (event as CustomEvent<{ duration?: number }> | undefined)?.detail;
        const duration = detail?.duration ?? 0.8;
        gsap.to(flying, { opacity: 1, duration, ease: 'power2.out', overwrite: 'auto' });
      };
      if (hero?.dataset.heroCoverStarted === 'true') reveal();
      else if (hero) hero.addEventListener('rhm-hero-cover-start', reveal, { once: true });
      else gsap.set(flying, { opacity: 1 });

      gsap.fromTo(
        flying,
        {
          xPercent: -50,
          yPercent: -50,
          x: () => getHeroSlotCentre().cx - getFlyingNaturalCentre().cx,
          y: () => getHeroSlotCentre().cy - getFlyingNaturalCentre().cy,
          scale: 1,
          letterSpacing: 0,
        },
        {
          xPercent: -50,
          yPercent: -50,
          x: 0,
          y: 0,
          scale: getEndScale,
          letterSpacing: '0.06em',
          ease: 'none',
          scrollTrigger: {
            trigger: '#hero',
            start: 'top top',
            end: '+=60vh',
            scrub: true,
            invalidateOnRefresh: true,
          },
        },
      );
      return () => hero?.removeEventListener('rhm-hero-cover-start', reveal);
    });
  });
}

function initClaim(gsap: GsapLike, root: HTMLElement) {
  if (!once(root)) return;
  const lockup = root.querySelector<HTMLElement>('#claim-heading .rhm-claim__lockup');
  const words = gsap.utils.toArray<HTMLElement>('[data-claim-word]', root);
  const ruled = root.querySelector<HTMLElement>('.rhm-claim__line--ruled');
  const ruleStart = root.querySelector<HTMLElement>('[data-claim-rule-start]');
  const ruleEnd = root.querySelector<HTMLElement>('[data-claim-rule-end]');
  if (!lockup || !words.length) return;

  gsap.context(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: reduce)', () => {
      gsap.set(words, { autoAlpha: 1, y: 0 });
      if (ruled) gsap.set(ruled, { '--claim-rule': 1 });
    });
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.set(words, { autoAlpha: 0, y: '0.45em' });
      if (ruled) gsap.set(ruled, { '--claim-rule': 0 });
      const tl = gsap.timeline({
        scrollTrigger: { trigger: lockup, start: 'top 80%', toggleActions: 'play none none none' },
      });
      tl.to(words, { autoAlpha: 1, y: 0, duration: WORD_DUR, ease: 'power2.out', stagger: WORD_STAGGER }, 0);
      if (ruled && ruleStart && ruleEnd) {
        const fromAt = Math.max(0, words.indexOf(ruleStart)) * WORD_STAGGER;
        const untilAt = Math.max(0, words.indexOf(ruleEnd)) * WORD_STAGGER;
        tl.to(ruled, { '--claim-rule': 1, duration: Math.max(untilAt - fromAt, 0.01), ease: 'none' }, fromAt);
      }
    });
  }, root);
}

function initCopy(gsap: GsapLike, SplitText: SplitTextLike, el: HTMLElement) {
  if (!once(el)) return;
  let played = false;
  let ctx: { revert: () => void } | null = null;

  const init = () => {
    ctx?.revert();
    ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: reduce)', () => {
        gsap.set(el, { visibility: 'visible' });
      });
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const blocks = gsap.utils
          .toArray<HTMLElement>(':scope > .rhm-body', el)
          .filter((node) => (node.textContent || '').trim());
        const targets = blocks.length ? blocks : [el];
        const split = SplitText.create(targets, {
          type: 'lines',
          linesClass: 'rhm-copy__line',
          mask: 'lines',
          tag: 'span',
          aria: 'auto',
        });
        const lines = split.lines;
        if (!lines.length) {
          gsap.set(el, { visibility: 'visible' });
          return;
        }
        gsap.set(split.masks, { display: 'block', overflow: 'clip', clipPath: 'inset(0)' });
        gsap.set(lines, { display: 'block' });
        gsap.set(el, { visibility: 'visible' });
        if (played) {
          gsap.set(lines, { yPercent: 0, autoAlpha: 1 });
          gsap.set(split.masks, { clipPath: 'inset(-0.2em 0)' });
          return;
        }
        const clearWillChange = () => gsap.set(lines, { clearProps: 'willChange' });
        gsap.fromTo(
          lines,
          { yPercent: 150, autoAlpha: 0 },
          {
            yPercent: 0,
            autoAlpha: 1,
            duration: LINE_DUR,
            stagger: LINE_STAGGER,
            ease: 'expo.out',
            overwrite: 'auto',
            onStart: () => {
              played = true;
              gsap.set(split.masks, { clipPath: 'inset(-0.2em 0)' });
              gsap.set(lines, { willChange: 'transform, opacity' });
            },
            onComplete: clearWillChange,
            onInterrupt: clearWillChange,
            scrollTrigger: { trigger: el, start: 'top 85%', once: true },
          },
        );
      });
    }, el);
  };

  init();
  let width = el.offsetWidth;
  let timer = 0;
  const observer = new ResizeObserver(() => {
    const next = el.offsetWidth;
    if (!next || next === width) return;
    width = next;
    window.clearTimeout(timer);
    timer = window.setTimeout(init, 200);
  });
  observer.observe(el);
}

function initCustom(gsap: GsapLike, mark: HTMLElement) {
  if (!once(mark)) return;
  gsap.context(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: reduce)', () => {
      gsap.set(mark, { '--mark': 1 });
    });
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.fromTo(
        mark,
        { '--mark': 0 },
        {
          '--mark': 1,
          duration: MARK_DUR,
          ease: 'power2.out',
          overwrite: 'auto',
          scrollTrigger: { trigger: mark, start: 'top 80%', toggleActions: 'play none none none' },
        },
      );
    });
  }, mark);
}

function initSolution(gsap: GsapLike, ScrollTrigger: ScrollTriggerLike, root: HTMLElement) {
  if (!once(root)) return;
  const desk = gsap.utils.toArray<HTMLElement>('.rhm-solution__cross--desk', root);
  const mobi = gsap.utils.toArray<HTMLElement>('.rhm-solution__cross--mobi', root);
  if (!desk.length && !mobi.length) return;
  for (const el of [...desk, ...mobi]) {
    if (el instanceof HTMLImageElement && !el.complete) {
      el.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
    }
  }
  const arm = (crosses: HTMLElement[]) => {
    gsap.set(crosses, { rotation: 0, transformOrigin: '50% 50%' });
    crosses.forEach((cross) => {
      gsap.to(cross, {
        rotation: 45,
        duration: TURN,
        ease: 'power2.out',
        overwrite: 'auto',
        scrollTrigger: { trigger: cross, start: 'center center', toggleActions: 'play none none reverse' },
      });
    });
  };
  gsap.context(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: reduce)', () => {
      gsap.set([...desk, ...mobi], { rotation: 0 });
    });
    mm.add('(prefers-reduced-motion: no-preference) and (min-width: 760px)', () => arm(desk));
    mm.add('(prefers-reduced-motion: no-preference) and (max-width: 759px)', () => arm(mobi));
  }, root);
}

function initRelief(gsap: GsapLike, root: HTMLElement) {
  if (!once(root)) return;
  const head = root.querySelector<HTMLElement>('[data-relief-head]');
  const track = root.querySelector<HTMLElement>('[data-relief-track]');
  const viewport = root.querySelector<HTMLElement>('[data-relief-viewport]');
  if (!head || !track || !viewport) return;
  const items = gsap.utils.toArray<HTMLElement>('[data-relief-item]', track);
  const count = Number(track.dataset.reliefCount || 0);
  if (!count || items.length < count * 3) return;
  const start = Number(track.dataset.reliefStart || 0);
  let cursor = count + start;
  let playing = false;
  let hold: { kill: () => void } | null = null;
  let step: TimelineLike | null = null;

  const railY = () => head.getBoundingClientRect().top - root.getBoundingClientRect().top;
  const yFor = (index: number) => railY() - (items[index]?.offsetTop ?? 0);
  const fitViewport = (index: number) => {
    const active = items[index];
    const last = items[index + VISIBLE_BELOW];
    if (!active || !last) return;
    viewport.style.height = `${Math.ceil(railY() + last.offsetTop - active.offsetTop + last.offsetHeight)}px`;
  };
  const paintActive = (index: number) => {
    items.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
      gsap.set(item, { opacity: reliefItemOpacity(i - index) });
    });
  };
  const stopLoop = () => {
    playing = false;
    hold?.kill();
    hold = null;
    step?.kill();
    step = null;
  };
  const place = (index: number, animate: boolean) => {
    fitViewport(index);
    const y = yFor(index);
    if (!animate) {
      gsap.set(track, { y });
      paintActive(index);
      return;
    }
    const prev = cursor;
    step?.kill();
    const timeline = gsap.timeline({
      onComplete: () => {
        if (index >= count * 2) {
          cursor = index - count;
          gsap.set(track, { y: yFor(cursor) });
          paintActive(cursor);
        } else {
          cursor = index;
        }
      },
    });
    step = timeline;
    timeline.to(track, { y, duration: STEP_DUR, ease: EASE, overwrite: 'auto' }, 0);
    items.forEach((item, i) => {
      const opacity = reliefItemOpacity(i - index);
      item.classList.toggle('is-active', i === index);
      if (Math.abs(opacity - reliefItemOpacity(i - prev)) < 0.001) return;
      timeline.to(item, { opacity, duration: STEP_DUR, ease: EASE, overwrite: 'auto' }, 0);
    });
  };
  const tick = () => {
    if (!playing) return;
    place(cursor + 1, true);
    hold = gsap.delayedCall(STEP_DUR + HOLD, tick);
  };
  const startLoop = () => {
    if (playing) return;
    playing = true;
    hold?.kill();
    hold = gsap.delayedCall(HOLD, tick);
  };

  gsap.context(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: reduce)', () => {
      place(cursor, false);
      return () => stopLoop();
    });
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.set(track, { force3D: true });
      place(cursor, false);
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) startLoop();
          else {
            stopLoop();
            place(cursor, false);
          }
        },
        { threshold: 0.35 },
      );
      io.observe(root);
      const onResize = () => place(cursor, false);
      window.addEventListener('resize', onResize);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') stopLoop();
        else if (root.getBoundingClientRect().top < window.innerHeight) startLoop();
      });
      return () => {
        io.disconnect();
        window.removeEventListener('resize', onResize);
        stopLoop();
      };
    });
  }, root);
}

function initInk(gsap: GsapLike) {
  const midpoint = document.querySelector<HTMLElement>('[data-rhm-midpoint]');
  const header = document.querySelector<HTMLElement>('.rhm-header');
  if (!midpoint || !header || header.dataset.rhmInk === 'on') return;
  header.dataset.rhmInk = 'on';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let current: 'light' | 'dark' = 'light';
  const apply = (ink: 'light' | 'dark', instant: boolean) => {
    if (ink === current && !instant) return;
    current = ink;
    const color = ink === 'dark' ? '#000000' : '#ffffff';
    document.documentElement.style.setProperty('--header-ink', color);
    document.body.dataset.headerInk = ink;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = color;
    gsap.to(header, { color, duration: instant ? 0 : 0.4, ease: 'power2.inOut', overwrite: 'auto' });
  };
  const sync = (instant = reduced) => {
    const passed = midpoint.getBoundingClientRect().top <= window.innerHeight * 0.5;
    apply(passed ? 'dark' : 'light', instant);
  };
  apply('light', true);
  sync(true);
  window.addEventListener('scroll', () => sync(), { passive: true });
  window.addEventListener('resize', () => sync(true));
}

async function boot() {
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  const SplitText = window.SplitText;
  if (!gsap || !ScrollTrigger || !SplitText) return;
  await fontsReady();
  ScrollTrigger.config({ ignoreMobileResize: true, limitCallbacks: true });

  const hero = document.getElementById('hero');
  if (hero) initHero(gsap, hero);
  const flying = document.querySelector<HTMLElement>('.rhm-header__wordmark-flying');
  if (flying) initWordmark(gsap, flying);
  const claim = document.getElementById('claim');
  if (claim) initClaim(gsap, claim);
  document.querySelectorAll<HTMLElement>('.rhm-copy__body').forEach((el) => initCopy(gsap, SplitText, el));
  document.querySelectorAll<HTMLElement>('.rhm-custom__mark').forEach((el) => initCustom(gsap, el));
  const solution = document.getElementById('solution');
  if (solution) initSolution(gsap, ScrollTrigger, solution);
  const relief = document.getElementById('relief');
  if (relief) initRelief(gsap, relief);
  initInk(gsap);
  ScrollTrigger.refresh();
}

function start() {
  const tick = () => {
    if (window.gsap && window.ScrollTrigger && window.SplitText) {
      void boot();
      return;
    }
    requestAnimationFrame(tick);
  };
  tick();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
window.addEventListener('load', () => window.ScrollTrigger?.refresh());
